import { useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import type { MaintenanceRequest } from "../../store/api/maintenanceApi";
import {
	useDeleteMaintenanceRequestMutation,
	useListMaintenanceRequestsQuery,
} from "../../store/api/maintenanceApi";
import { arrayToCsv, downloadCsv } from "../../utils/csvExport";
import BulkActions from "./components/BulkActions";
import CreateMaintenanceModal from "./components/CreateMaintenanceModal";
import EditMaintenanceModal from "./components/EditMaintenanceModal";
import MaintenanceDetailModal from "./components/MaintenanceDetailModal";
import MaintenanceFilters from "./components/MaintenanceFilters";
import MaintenanceStats from "./components/MaintenanceStats";
import MaintenanceTable from "./components/MaintenanceTable";

const MaintenanceManagement = () => {
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState({
		status: "",
		priority: "",
	});
	const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
	const [selectedRequest, setSelectedRequest] =
		useState<MaintenanceRequest | null>(null);
	const [requestToEdit, setRequestToEdit] = useState<MaintenanceRequest | null>(
		null,
	);
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);

	const {
		data: maintenanceData,
		isLoading,
		error,
		refetch,
	} = useListMaintenanceRequestsQuery({
		page,
		page_size: 20,
		status: (filters.status as any) || undefined,
		priority: (filters.priority as any) || undefined,
		ordering: "-requested_date",
	});

	const [deleteMaintenance, { isLoading: isDeleting }] =
		useDeleteMaintenanceRequestMutation();

	const handleFilterChange = (newFilters: {
		status?: string;
		priority?: string;
	}) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
		setPage(1);
	};

	const handleRequestSelect = (requestId: string, selected: boolean) => {
		if (selected) {
			setSelectedRequests((prev) => [...prev, requestId]);
		} else {
			setSelectedRequests((prev) => prev.filter((id) => id !== requestId));
		}
	};

	const handleSelectAll = (selected: boolean) => {
		if (!maintenanceData) return;
		if (selected) {
			setSelectedRequests(maintenanceData.results.map((r) => r.id));
		} else {
			setSelectedRequests([]);
		}
	};

	const handleRequestClick = (request: MaintenanceRequest) => {
		setSelectedRequest(request);
		setIsDetailModalOpen(true);
	};

	const handleEditRequest = (request: MaintenanceRequest) => {
		setRequestToEdit(request);
		setIsEditModalOpen(true);
	};

	const handleDeleteRequest = async (requestId: string) => {
		if (
			!window.confirm(
				"Are you sure you want to delete this maintenance request?",
			)
		) {
			return;
		}

		try {
			await deleteMaintenance(requestId).unwrap();
			refetch();
			setSelectedRequests((prev) => prev.filter((id) => id !== requestId));
		} catch (error) {
			console.error("Failed to delete maintenance request:", error);
			alert("Failed to delete maintenance request. Please try again.");
		}
	};

	const handleBulkAction = async (action: string) => {
		switch (action) {
			case "export":
				if (!maintenanceData) return;
				const csvData = maintenanceData.results.map((request) => ({
					Title: request.title || "",
					Property: request.property_name || "N/A",
					Priority: request.priority || "",
					Status: request.status || "",
					"Requested By": request.requested_by_name || "N/A",
					"Requested Date": request.requested_date || "",
				}));

				const csvContent = arrayToCsv(csvData, [
					"Title",
					"Property",
					"Priority",
					"Status",
					"Requested By",
					"Requested Date",
				]);
				downloadCsv(csvContent, "maintenance-export");
				break;
			case "delete":
				if (
					!window.confirm(
						`Are you sure you want to delete ${selectedRequests.length} maintenance requests?`,
					)
				) {
					return;
				}
				try {
					await Promise.all(
						selectedRequests.map((id) => deleteMaintenance(id).unwrap()),
					);
					refetch();
					setSelectedRequests([]);
				} catch (error) {
					console.error("Failed to delete maintenance requests:", error);
					alert(
						"Failed to delete some maintenance requests. Please try again.",
					);
				}
				break;
			default:
				break;
		}
	};

	if (error) {
		return (
			<div className="w-full flex flex-col items-center justify-center min-h-screen space-y-4">
				<Icon name="AlertCircle" size={48} className="text-destructive" />
				<h2 className="text-xl font-semibold text-foreground">
					Error Loading Maintenance Requests
				</h2>
				<p className="text-muted-foreground">
					Failed to load maintenance requests. Please try again.
				</p>
				<Button onClick={() => refetch()}>Retry</Button>
			</div>
		);
	}

	const requests = maintenanceData?.results || [];

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="mb-4 sm:mb-6">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
						<div className="min-w-0 flex-1">
							<h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground truncate">
								Maintenance Management
							</h1>
							<p className="text-sm sm:text-base text-muted-foreground mt-1 break-words">
								Manage maintenance requests, assign technicians, and track
								resolutions.
							</p>
						</div>

						<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto">
							<Button
								variant="outline"
								onClick={() => handleBulkAction("export")}
								iconName="Download"
								iconPosition="left"
								className="w-full sm:w-auto"
								size="sm"
							>
								Export
							</Button>

							<Button
								onClick={() => setIsCreateModalOpen(true)}
								iconName="Plus"
								iconPosition="left"
								className="w-full sm:w-auto"
								size="sm"
							>
								Create
							</Button>
						</div>
					</div>
				</div>

				{/* Main Content Grid */}
				<div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6">
					{/* Filters & Stats - Full width on mobile, 3 cols on desktop */}
					<div className="xl:col-span-3 space-y-4 sm:space-y-6">
						<MaintenanceFilters
							filters={filters}
							onFilterChange={handleFilterChange}
							requestCount={requests.length}
						/>

						<MaintenanceStats requests={requests} />
					</div>

					{/* Main Table Area - 9 cols on desktop */}
					<div className="xl:col-span-9 space-y-4 sm:space-y-6">
						{selectedRequests.length > 0 && (
							<BulkActions
								selectedCount={selectedRequests.length}
								onBulkAction={handleBulkAction}
								onClearSelection={() => setSelectedRequests([])}
							/>
						)}

						{isLoading ? (
							<Loader
								size="lg"
								showText
								text="Loading maintenance requests..."
								className="h-64"
							/>
						) : (
							<>
								<MaintenanceTable
									requests={requests}
									selectedRequests={selectedRequests}
									onRequestSelect={handleRequestSelect}
									onSelectAll={handleSelectAll}
									onRequestClick={handleRequestClick}
									onEditRequest={handleEditRequest}
									onDeleteRequest={handleDeleteRequest}
								/>

								{/* Pagination */}
								{maintenanceData &&
									(maintenanceData.next || maintenanceData.previous) && (
										<div className="flex items-center justify-between gap-2 sm:gap-4">
											<Button
												variant="outline"
												onClick={() => setPage((p) => Math.max(1, p - 1))}
												disabled={!maintenanceData.previous}
												iconName="ChevronLeft"
												iconPosition="left"
												size="sm"
												className="flex-1 sm:flex-none"
											>
												<span className="hidden sm:inline">Previous</span>
												<span className="sm:hidden">Prev</span>
											</Button>
											<span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
												Page {page} of {Math.ceil(maintenanceData.count / 20)}
											</span>
											<Button
												variant="outline"
												onClick={() => setPage((p) => p + 1)}
												disabled={!maintenanceData.next}
												iconName="ChevronRight"
												iconPosition="right"
												size="sm"
												className="flex-1 sm:flex-none"
											>
												Next
											</Button>
										</div>
									)}

								{/* Empty State */}
								{requests.length === 0 && (
									<div className="bg-card rounded-lg border border-border p-6 sm:p-12 text-center overflow-hidden">
										<Icon
											name="Wrench"
											size={48}
											className="sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-4"
										/>
										<h3 className="text-base sm:text-lg font-medium text-foreground mb-2">
											No maintenance requests found
										</h3>
										<p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
											No maintenance requests match your current filters. Try
											adjusting your search criteria.
										</p>
										<Button
											onClick={() => setIsCreateModalOpen(true)}
											iconName="Plus"
											iconPosition="left"
											size="sm"
										>
											Create First Request
										</Button>
									</div>
								)}
							</>
						)}
					</div>
				</div>

				{/* Maintenance Detail Modal */}
				<MaintenanceDetailModal
					request={selectedRequest}
					isOpen={isDetailModalOpen}
					onClose={() => {
						setIsDetailModalOpen(false);
						setSelectedRequest(null);
					}}
					onUpdate={refetch}
					onEdit={handleEditRequest}
				/>

				{/* Create Maintenance Modal */}
				<CreateMaintenanceModal
					isOpen={isCreateModalOpen}
					onClose={() => setIsCreateModalOpen(false)}
					onSuccess={() => {
						setIsCreateModalOpen(false);
						refetch();
					}}
				/>

				{/* Edit Maintenance Modal */}
				<EditMaintenanceModal
					isOpen={isEditModalOpen}
					onClose={() => {
						setIsEditModalOpen(false);
						setRequestToEdit(null);
					}}
					onSuccess={() => {
						setIsEditModalOpen(false);
						setRequestToEdit(null);
						refetch();
					}}
					request={requestToEdit}
				/>
			</div>
		</div>
	);
};

export default MaintenanceManagement;
